import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Row, Col } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  initiateAsyncPayment as initiateAsyncPaymentAction,
  getLedgerId,
  getIsInitiatingAsyncPayment,
  getAsyncPaymentData,
  getMoveInUnitEventId,
} from '../../../../../../src/web/store/modules/movein';
import {
  TRACK_CHECKOUT_STEP,
  trackDataLayerValue,
} from '../../../../../../src/web/utils/dataLayer';
import AsyncPaymentHandler from '../AsyncPaymentHandler/AsyncPaymentHandler';
import intlPath from '../../../../../../src/web/utils/intlPath';
import Translation from '../../../../../components/stateless/Translation';
import SMSearchResultsLoadingIndicator from '../../../../components/stateless/SMSearchResultsLoadingIndicator';
import { getBrandCode } from '../../../../../../constants/smBrandNames';

const propTypes = {
  asyncPaymentData: PropTypes.shape({
    success: PropTypes.bool.isRequired,
    error: PropTypes.string,
    mfeSrcUrl: PropTypes.string,
    paymentIntentId: PropTypes.string,
  }).isRequired,
  checkoutConfirmationPath: PropTypes.string.isRequired,
  facility: PropTypes.shape().isRequired,
  initiateAsyncPayment: PropTypes.func.isRequired,
  isInitiatingAsyncPayment: PropTypes.bool.isRequired,
  ledgerId: PropTypes.string.isRequired,
  moveInUnitEventId: PropTypes.string.isRequired,
  receipt: PropTypes.shape({
    totalAmount: PropTypes.number.isRequired,
  }).isRequired,
};

const defaultProps = {};

const UkPaymentForm = ({
  asyncPaymentData,
  checkoutConfirmationPath,
  facility,
  initiateAsyncPayment,
  isInitiatingAsyncPayment,
  ledgerId,
  moveInUnitEventId,
  receipt,
}) => {
  const intl = useIntl();
  const [hasTrackedDataLayer, setHasTrackedDataLayer] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!hasTrackedDataLayer && facility) {
      setHasTrackedDataLayer(true);
      TRACK_CHECKOUT_STEP(
        get(facility, 'unitGroup'),
        facility,
        'new_booking_flow_step4',
        'payment',
      );
    }

    if (facility && ledgerId) {
      initiateAsyncPayment(facility.id, ledgerId, receipt?.totalAmount);
    }
  }, [facility, hasTrackedDataLayer, initiateAsyncPayment, ledgerId, receipt?.totalAmount]);

  const onPaymentIntentSuccess = useCallback((additionalQueryParams = {}) => {
    const baseQueryString = `facilityId=${facility.id}&confirmationId=${moveInUnitEventId}`;
    const additionalParams = Object.entries(additionalQueryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const queryString = additionalParams
      ? `${baseQueryString}&${additionalParams}`
      : baseQueryString;

    if (additionalQueryParams.paymentInitFailed) {
      trackDataLayerValue({
        event: 'fmi_mfe_failure',
        category_rin: 'Failure',
        action_rin: 'FMI',
        brand: getBrandCode(get(facility, 'brandName')),
        storeId: get(facility, 'storeNumber'),
      });
    } else {
      trackDataLayerValue({
        event: 'fmi_success',
        category_rin: 'Success',
        action_rin: 'FMI',
        brand: getBrandCode(get(facility, 'brandName')),
        storeId: get(facility, 'storeNumber'),
      });
    }

    window.location = `${intlPath(intl.locale, checkoutConfirmationPath)}?${queryString}`;
  }, [facility, moveInUnitEventId, intl.locale, checkoutConfirmationPath]);

  useEffect(() => {
    if (asyncPaymentData && !asyncPaymentData.success && !isInitiatingAsyncPayment) {
      if (retryCount < 2) {
        // Retry the payment initiation
        const timer = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          initiateAsyncPayment(facility.id, ledgerId, receipt?.totalAmount);
        }, 1000); // Wait 1 second before retrying

        return () => clearTimeout(timer);
      }

      // After 2 additional failed attempts (3 total with the initial attempt)
      // redirect to confirmation page with additional query param
      onPaymentIntentSuccess({ paymentInitFailed: true });
    } else if (asyncPaymentData && asyncPaymentData.success) {
      // Reset retry count on success
      setRetryCount(0);
    }

    return undefined;
  }, [
    asyncPaymentData,
    isInitiatingAsyncPayment,
    retryCount,
    facility,
    ledgerId,
    receipt?.totalAmount,
    initiateAsyncPayment,
    onPaymentIntentSuccess,
  ]);

  return (
    <form>
      <Row>
        <Col xs={12}>
          {isInitiatingAsyncPayment || asyncPaymentData?.success === false ? (
            <div className="text-center">
              <SMSearchResultsLoadingIndicator />
              <Translation
                id="widgets.checkout.ukPaymentForm.loadingPaymentProvider"
                defaultMessage="Establishing connection to payment provider..."
              />
            </div>
          ) : null}
          {asyncPaymentData && asyncPaymentData.success ? (
            <AsyncPaymentHandler
              mfeSrcUrl={asyncPaymentData.mfeSrcUrl}
              paymentIntentId={asyncPaymentData.paymentIntentId}
              onPaymentIntentSuccess={onPaymentIntentSuccess}
              currentFacility={facility}
              loadingMessage={(
                <Translation
                  id="widgets.checkout.ukPaymentForm.sendingPayment"
                  defaultMessage="Sending payment information..."
                />
              )}
              syncMessage={(
                <Translation
                  id="widgets.checkout.ukPaymentForm.syncingPayment"
                  defaultMessage="Payment successful. Retrieving payment information..."
                />
              )}
            />
          ) : null}
        </Col>
      </Row>
    </form>
  );
};

UkPaymentForm.propTypes = propTypes;
UkPaymentForm.defaultProps = defaultProps;

const mapStateToProps = state => ({
  ledgerId: getLedgerId(state),
  isInitiatingAsyncPayment: getIsInitiatingAsyncPayment(state),
  asyncPaymentData: getAsyncPaymentData(state),
  moveInUnitEventId: getMoveInUnitEventId(state),
});

const mapDispatchToProps = {
  initiateAsyncPayment: initiateAsyncPaymentAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UkPaymentForm);
